import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import KgVis from "../../components/KgVisV2";
import AssociationTable from "../../components/AssociationTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Container, Row, Col, Alert, Button } from "react-bootstrap";
import { beApiUrl } from "../../api/be_config";
import _ from "lodash";
import './Graphshow.css'; // Import custom styles

export default function Graphshow() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryData = {
    entity_one_name: queryParams.get("nodeonename"),
    entity_one_type: queryParams.get("nodeonetype"),
    entity_two_name: queryParams.get("nodetwoname"),
    entity_two_type: queryParams.get("nodetwotype"),
    relation: queryParams.get("relationtype"),
    db_identifier: queryParams.get("db_identifier"),
    size_limit: 20,
  };


  const [edges, setEdges] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [triplets, setTriplets] = useState([]);
  const [rdyRender, setRdyRender] = useState(false);
  const [isLoadingMainGraph, setIsLoadingMainGraph] = useState(true);

  const [selectedNode, setSelectedNode] = useState(null);
  const [subgraph, setSubgraph] = useState({ nodes: [], edges: [], triplets: [] });
  const [twoHopPaths, setTwoHopPaths] = useState([]);
  const [isLoadingPaths, setIsLoadingPaths] = useState(false);
  const [entityOneId, setEntityOneId] = useState(null);
  const [pathCount, setPathCount] = useState(0);
  const [pathError, setPathError] = useState(null);
  const [twoHopGraph, setTwoHopGraph] = useState({ nodes: [], edges: [] });

  // Calculate in-degrees for each node
  const calculateInDegrees = (edges) => {
    const inDegrees = {};
    edges.forEach(edge => {
      if (!inDegrees[edge.target]) {
        inDegrees[edge.target] = 0;
      }
      inDegrees[edge.target]++;
    });
    return inDegrees;
  };

  // Fetch data from the backend
  useEffect(() => {
    setIsLoadingMainGraph(true);
    fetch(beApiUrl + "kgpage/kgquery/v1/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(queryData),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to get the relations.");
        return response.json();
      })
      .then((data) => {
        if (!_.isEmpty(data)) {
          const inDegrees = calculateInDegrees(data.edges);
          const updatedEdges = data.edges.map(edge => ({
            ...edge,
            width: Math.min(5, Math.max(1, inDegrees[edge.target] || 1)), // Set width based on in-degree
          }));
          
          // for (let i = 0; i < data.triplets.length; i++) {
          //   const triplet = data.triplets[i];
          //   console.log("triplet_entity_two_name", triplet.entity_two_name.toLowerCase());
          // }
          
          for (let i = 0; i < data.nodes.length; i++) {
            const node = data.nodes[i]; 
            data.triplets.forEach(item => {
              if (item.entity_two_name.toLowerCase() === node.label.toLowerCase() || item.entity_one_name.toLowerCase() === node.label.toLowerCase()) {
                data.nodes[i] = {
                  ...data.nodes[i],
                  label_id: item.entity_two_id
                }
              }
            });
          }
          
          console.log("nodes",data.nodes);
          console.log("triplets",data.triplets);
          setNodes(data.nodes);
          setEdges(updatedEdges);
          setTriplets(data.triplets);
          
          // Store entity_one_id for later use in two-hop paths query
          if (data.nodes.length > 0) {
            const entityOne = data.nodes.find(node => node.label === queryData.entity_one_name);
            if (entityOne) {
              setEntityOneId(entityOne.id);
            }
          }
          
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsLoadingMainGraph(false);
      });
  }, []);
  
  // Check if data is ready to render
  useEffect(() => {
    if (nodes.length > 0 && edges.length > 0) setRdyRender(true);
  }, [nodes, edges]);

  // Handle node click
  const handleNodeClick = (node) => {
    setSelectedNode(node);
    calculateSubgraph(node);
    fetchTwoHopPaths(node);
  };

  // Fetch two-hop paths between entity_one and the selected node
  const fetchTwoHopPaths = (targetNode) => {
    if (!entityOneId || !targetNode) return;
    
    setIsLoadingPaths(true);
    setTwoHopPaths([]);
    setPathCount(0);
    setPathError(null);
    setTwoHopGraph({ nodes: [], edges: [] });
    
    let targetTriplet = triplets.filter(item => item.entity_one_name === targetNode.label || item.entity_two_name === targetNode.label);
    targetTriplet = targetTriplet[0];
    const pathQueryData = {
      entity_one_id: targetTriplet.entity_one_id,
      entity_two_id: targetTriplet.entity_two_id,
      size_limit: 100,
      db_identifier: queryData.db_identifier ? parseInt(queryData.db_identifier) : 0
    };
    
    
    fetch(beApiUrl + "kgpage/two_hop_paths/v1/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(pathQueryData),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to get two-hop paths.");
        return response.json();
      })
      .then((data) => {
        if (data && data.paths) {
          setTwoHopPaths(data.paths);
          setPathCount(data.path_count || data.paths.length);
          
          // 使用返回的nodes和edges数据绘制子图
          if (data.nodes && data.edges) {
            setTwoHopGraph({
              nodes: data.nodes,
              edges: data.edges
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching two-hop paths:", error);
        setPathError("Failed to fetch paths. Please try again.");
      })
      .finally(() => {
        setIsLoadingPaths(false);
      });
  };

  // Calculate subgraph based on selected node label
  const calculateSubgraph = (node) => {
    const relatedEdges = edges.filter(
      (edge) => edge.source === node.id || edge.target === node.id
    );

    const relatedNodeIds = new Set(
      relatedEdges.flatMap((edge) => [edge.source, edge.target])
    );
    
    const relatedNodes = nodes.filter((node) => relatedNodeIds.has(node.id));

    // Filter triplets based on node.label instead of ID
    const relatedTriplets = triplets.filter((triplet) => {
      return (
        triplet.entity_one_name === node.label || triplet.entity_two_name === node.label
      );
    });

    setSubgraph({ nodes: relatedNodes, edges: relatedEdges, triplets: relatedTriplets });
  };
  
  return (
    <Container>
      <Row>
        <Col xs={8}>
          <div className="main-graph-container">
            {isLoadingMainGraph ? (
              <LoadingSpinner 
                size="xl"
                variant="primary"
                title="Loading Knowledge Graph"
                subtitle="Please wait while we fetch the data"
                minHeight="500px"
              />
            ) : rdyRender ? (
              <KgVis nodes={nodes} edges={edges} onNodeClick={handleNodeClick} />
            ) : (
              <Alert variant="warning" className="no-data-alert">
                No data available to display the knowledge graph.
              </Alert>
            )}
          </div>
        </Col>
        <Col xs={4}>
          <div className="right-panel">
            {selectedNode ? (
              <Alert variant="info" className="subgraph-alert">
                <h5 className="subgraph-title">
                  Subgraph: {selectedNode.label} ({selectedNode.id})
                </h5>

                {/* Scrollable Triplet List */}
                <div className="subgraph-triplets">
                  <h6 className="section-title">Related Triplets</h6>
                  <div className="triplet-list-container">
                    <Col>
                      {rdyRender && <AssociationTable triplets={subgraph.triplets} />}
                    </Col>
                  </div>
                </div>

                {/* Two-hop path visualization with loading state */}
                <div className="two-hop-visualization">
                  <h6 className="section-title">Two-hop Path Visualization</h6>
                  <div className="two-hop-graph-container">
                    {isLoadingPaths ? (
                      <LoadingSpinner 
                        size="md"
                        variant="info"
                        title="Loading Two-hop Paths"
                        minHeight="200px"
                        className="subgraph-loading"
                      />
                    ) : twoHopGraph.nodes.length > 0 ? (
                      <KgVis nodes={twoHopGraph.nodes} edges={twoHopGraph.edges} />
                    ) : (
                      <div className="no-subgraph-data">
                        <small className="text-muted">No two-hop path data available</small>
                      </div>
                    )}
                  </div>
                </div>
                
              </Alert>
            ) : (
              <div className="placeholder-panel">
                <Alert variant="secondary" className="placeholder-alert">
                  <div className="placeholder-content">
                    <h6>Node Selection</h6>
                    <p>Click on a node to view its connected subgraph and two-hop path visualization.</p>
                  </div>
                </Alert>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          {rdyRender && <AssociationTable triplets={triplets} />}
        </Col>
      </Row>
    </Container>
  );
}
